import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { OverflowList } from '@theme/overflow-list';
import { yieldToMainThread, getViewParameterValue, ResizeNotifier } from '@theme/utilities';

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets - The fieldset elements.
 * @property {HTMLElement} [overflowList] - The overflow list element.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  #resizeObserver = new ResizeNotifier(() => this.updateVariantPickerCss());

  /** Swatch / material tooltip: horizontal nudge so popovers stay inside the viewport. */
  /** @type {HTMLElement | null} */
  #activeSwatchTooltipLabel = null;

  /** @type {(() => void) | null} */
  #scrollRepositionHandler = null;

  #swatchTooltipViewportMargin = 12;

  /**
   * One entry per variant fieldset — keeps `fieldsetIndex`, `#radios`, and `#checkedIndices` aligned.
   * Also syncs `data-current-checked` so swatch `:has(:checked)` and outline styles stay in sync after morph/API updates.
   */
  #hydrateRadioStateFromDom() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    this.#radios = [];
    this.#checkedIndices = [];

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input[type="radio"]') ?? []).filter(
        /** @returns {radio is HTMLInputElement} */
        (radio) => radio instanceof HTMLInputElement
      );

      this.#radios.push(radios);

      /* Prefer native :checked — it drives swatch outline (:has(:checked)). Dataset can drift after morph
         (onBeforeUpdate preserves data-current-checked) while the checked property comes from server HTML. */
      let selectedIndex = radios.findIndex((radio) => radio.checked);
      if (selectedIndex === -1) {
        selectedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
      }

      if (selectedIndex !== -1) {
        radios.forEach((radio, idx) => {
          radio.dataset.currentChecked = idx === selectedIndex ? 'true' : 'false';
          radio.dataset.previousChecked = 'false';
        });
        radios[selectedIndex].checked = true;
        this.#checkedIndices.push([selectedIndex]);
      } else {
        this.#checkedIndices.push([]);
      }
    });

    const sizeChecked =
      /** @type {HTMLInputElement | null} */
      this.querySelector('input[data-option-name-handle="size"]:checked');
    if (sizeChecked instanceof HTMLInputElement) {
      this.#syncExpandedMaterialRadiosForCombinedOptions(sizeChecked);
    }
  }

  connectedCallback() {
    super.connectedCallback();

    this.#hydrateRadioStateFromDom();

    this.addEventListener('change', this.variantChanged.bind(this));
    this.#resizeObserver.observe(this);
    this.#bindSwatchTooltipClamp();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#unbindSwatchTooltipClamp();
    this.#resizeObserver.disconnect();
  }

  /**
   * Product cards use overflow:hidden on ancestors; fixed tooltips escape that clipping.
   * @returns {boolean}
   */
  #usesFixedSwatchTooltips() {
    return Boolean(this.closest('product-card'));
  }

  /**
   * @param {HTMLElement} label
   * @param {HTMLElement} tip
   */
  #positionSwatchTooltip(label, tip) {
    if (this.#usesFixedSwatchTooltips()) {
      this.#positionFixedSwatchTooltip(label, tip);
      return;
    }

    this.#clearFixedSwatchTooltipStyles(tip);
    this.#clampSwatchTooltipShift(label, tip);
  }

  /**
   * @param {HTMLElement} label
   * @param {HTMLElement} tip
   */
  #positionFixedSwatchTooltip(label, tip) {
    tip.classList.add('variant-option__tooltip--fixed');

    tip.style.setProperty('visibility', 'hidden');
    tip.style.setProperty('opacity', '1');
    tip.style.setProperty('position', 'fixed');
    tip.style.setProperty('left', '-10000px');
    tip.style.setProperty('top', '0');

    const tipWidth = tip.offsetWidth;
    const tipHeight = tip.offsetHeight;

    tip.style.removeProperty('visibility');
    tip.style.removeProperty('opacity');

    if (!tipWidth || !tipHeight) {
      this.#clearFixedSwatchTooltipStyles(tip);
      return;
    }

    const gap = 8;
    const arrow = 9;
    const viewportWidth = window.visualViewport?.width ?? window.innerWidth;
    const margin = this.#swatchTooltipViewportMargin;
    const labelRect = label.getBoundingClientRect();
    const cx = labelRect.left + labelRect.width / 2;

    let left = cx - tipWidth / 2;
    const minLeft = margin;
    const maxLeft = viewportWidth - margin - tipWidth;
    left = Math.max(minLeft, Math.min(left, maxLeft));

    const top = labelRect.top - tipHeight - gap - arrow;
    const arrowLeft = Math.round(cx - left);

    tip.style.setProperty('left', `${Math.round(left)}px`);
    tip.style.setProperty('top', `${Math.round(top)}px`);
    tip.style.setProperty('bottom', 'auto');
    tip.style.setProperty('transform', 'none');
    tip.style.setProperty('--tooltip-arrow-left', `${arrowLeft}px`);
    label.style.removeProperty('--tooltip-shift-x');
  }

  /** @param {HTMLElement} tip */
  #clearFixedSwatchTooltipStyles(tip) {
    tip.classList.remove('variant-option__tooltip--fixed');
    tip.style.removeProperty('position');
    tip.style.removeProperty('left');
    tip.style.removeProperty('top');
    tip.style.removeProperty('bottom');
    tip.style.removeProperty('transform');
    tip.style.removeProperty('visibility');
    tip.style.removeProperty('opacity');
    tip.style.removeProperty('--tooltip-arrow-left');
  }

  /**
   * @param {HTMLElement} label
   * @param {HTMLElement} tip
   */
  #clampSwatchTooltipShift(label, tip) {
    const w = tip.offsetWidth || tip.getBoundingClientRect().width;
    if (!w) {
      label.style.removeProperty('--tooltip-shift-x');
      return;
    }

    const viewportWidth = window.visualViewport?.width ?? window.innerWidth;
    const margin = this.#swatchTooltipViewportMargin;
    const labelRect = label.getBoundingClientRect();
    const cx = labelRect.left + labelRect.width / 2;
    const idealLeft = cx - w / 2;
    let minLeft = margin;
    let maxLeft = viewportWidth - margin - w;

    const hostRect = this.getBoundingClientRect();
    if (hostRect.width >= w + margin * 2) {
      minLeft = Math.max(minLeft, Math.round(hostRect.left + margin));
      maxLeft = Math.min(maxLeft, Math.round(hostRect.right - margin - w));
    }

    if (maxLeft < minLeft) {
      minLeft = margin;
      maxLeft = viewportWidth - margin - w;
    }

    const clampedLeft = Math.max(minLeft, Math.min(idealLeft, maxLeft));
    const shiftPx = Math.round(clampedLeft - idealLeft);

    label.style.setProperty('--tooltip-shift-x', `${shiftPx}px`);
  }

  #repositionActiveSwatchTooltip() {
    const label = this.#activeSwatchTooltipLabel;
    if (!label?.isConnected) {
      this.#activeSwatchTooltipLabel = null;
      this.#detachSwatchTooltipScrollListener();
      return;
    }

    const tip = label.querySelector('.variant-option__material-tooltip, .variant-option__swatch-tooltip');
    if (tip instanceof HTMLElement) {
      this.#positionSwatchTooltip(label, tip);
    }
  }

  /** @param {HTMLElement | null | undefined} label */
  #clearSwatchTooltipShift(label) {
    const target = label ?? this.#activeSwatchTooltipLabel;
    if (target?.isConnected) {
      target.style.removeProperty('--tooltip-shift-x');
      const tip = target.querySelector('.variant-option__material-tooltip, .variant-option__swatch-tooltip');
      if (tip instanceof HTMLElement) {
        this.#clearFixedSwatchTooltipStyles(tip);
      }
    }

    if (!label || label === this.#activeSwatchTooltipLabel) {
      this.#activeSwatchTooltipLabel = null;
      this.#detachSwatchTooltipScrollListener();
    }
  }

  #detachSwatchTooltipScrollListener() {
    if (!this.#scrollRepositionHandler) return;
    window.removeEventListener('scroll', this.#scrollRepositionHandler, true);
    window.removeEventListener('resize', this.#scrollRepositionHandler);
    window.visualViewport?.removeEventListener('scroll', this.#scrollRepositionHandler);
    window.visualViewport?.removeEventListener('resize', this.#scrollRepositionHandler);
    this.#scrollRepositionHandler = null;
  }

  #ensureSwatchTooltipScrollListener() {
    if (this.#scrollRepositionHandler) return;
    this.#scrollRepositionHandler = () => this.#repositionActiveSwatchTooltip();
    window.addEventListener('scroll', this.#scrollRepositionHandler, { capture: true, passive: true });
    window.addEventListener('resize', this.#scrollRepositionHandler);
    window.visualViewport?.addEventListener('scroll', this.#scrollRepositionHandler, { passive: true });
    window.visualViewport?.addEventListener('resize', this.#scrollRepositionHandler, { passive: true });
  }

  #bindSwatchTooltipClamp() {
    this.addEventListener('pointerover', this.#onPointerOverSwatchTooltip);
    this.addEventListener('pointerout', this.#onPointerOutSwatchTooltip);
    this.addEventListener('focusin', this.#onFocusInSwatchTooltip);
    this.addEventListener('focusout', this.#onFocusOutSwatchTooltip);
  }

  #unbindSwatchTooltipClamp() {
    this.removeEventListener('pointerover', this.#onPointerOverSwatchTooltip);
    this.removeEventListener('pointerout', this.#onPointerOutSwatchTooltip);
    this.removeEventListener('focusin', this.#onFocusInSwatchTooltip);
    this.removeEventListener('focusout', this.#onFocusOutSwatchTooltip);
    this.#detachSwatchTooltipScrollListener();
    this.#clearSwatchTooltipShift();
  }

  /** @param {PointerEvent} event */
  #onPointerOverSwatchTooltip = (event) => {
    if (!(event.target instanceof Element)) return;
    const label = event.target.closest('label.variant-option__button-label--has-swatch');
    if (!label || !this.contains(label)) return;

    const tip = label.querySelector('.variant-option__material-tooltip, .variant-option__swatch-tooltip');
    if (!(tip instanceof HTMLElement)) return;

    this.#activeSwatchTooltipLabel = label;
    requestAnimationFrame(() => {
      if (this.#activeSwatchTooltipLabel === label) {
        this.#positionSwatchTooltip(label, tip);
      }
    });
    this.#ensureSwatchTooltipScrollListener();
  };

  /** @param {PointerEvent} event */
  #onPointerOutSwatchTooltip = (event) => {
    if (!(event.target instanceof Element)) return;
    const label = event.target.closest('label.variant-option__button-label--has-swatch');
    if (!label || !this.contains(label)) return;

    const { relatedTarget } = event;
    if (relatedTarget instanceof Node && label.contains(relatedTarget)) return;

    if (this.#activeSwatchTooltipLabel === label) {
      this.#clearSwatchTooltipShift(label);
    }
  };

  /** @param {FocusEvent} event */
  #onFocusInSwatchTooltip = (event) => {
    if (!(event.target instanceof Element)) return;
    const label = event.target.closest('label.variant-option__button-label--has-swatch');
    if (!label || !this.contains(label)) return;

    const tip = label.querySelector('.variant-option__material-tooltip, .variant-option__swatch-tooltip');
    if (!(tip instanceof HTMLElement)) return;

    this.#activeSwatchTooltipLabel = label;
    requestAnimationFrame(() => {
      if (this.#activeSwatchTooltipLabel === label) {
        this.#positionSwatchTooltip(label, tip);
      }
    });
    this.#ensureSwatchTooltipScrollListener();
  };

  /** @param {FocusEvent} event */
  #onFocusOutSwatchTooltip = (event) => {
    if (!(event.target instanceof Element)) return;
    const label = event.target.closest('label.variant-option__button-label--has-swatch');
    if (!label || !this.contains(label)) return;

    const { relatedTarget } = event;
    if (relatedTarget instanceof Node && label.contains(relatedTarget)) return;

    if (this.#activeSwatchTooltipLabel === label) {
      this.#clearSwatchTooltipShift(label);
    }
  };

  /**
   * Material + plate combos render many radios with the same `value` but different
   * `data-linked-size-option-value-id` / linked plate. When SIZE (or plate) alone
   * changes we radio-check the material row that matches the new selection.
   */
  #getActivePlateOptionValueId() {
    const plateRadio = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[data-option-name-handle="plate"]:checked')
    );

    /** @type {string | undefined} */
    let id = plateRadio?.dataset.optionValueId ? String(plateRadio.dataset.optionValueId) : undefined;

    const hasPlateInputs = Boolean(this.querySelector('input[data-option-name-handle="plate"]'));
    if (!id && !hasPlateInputs && this.dataset.hiddenPlateOptionValueId) {
      id = String(this.dataset.hiddenPlateOptionValueId);
    }

    return id;
  }

  /**
   * @param {HTMLElement} changedActiveTarget
   */
  #syncExpandedMaterialRadiosForCombinedOptions(changedActiveTarget) {
    if (!(changedActiveTarget instanceof HTMLElement)) return;

    const handle = changedActiveTarget.dataset.optionNameHandle;
    if (handle !== 'size' && handle !== 'plate') return;

    let materialChecked =
      /** @type {HTMLInputElement | null} */ this.querySelector('input[data-option-name-handle="material"]:checked');

    if (!(materialChecked instanceof HTMLInputElement)) {
      materialChecked =
        /** @type {HTMLInputElement | null} */ (
          this.querySelector('input[data-option-name-handle="material"][data-current-checked="true"]')
        );
    }

    if (!(materialChecked instanceof HTMLInputElement)) return;

    const materialValue = materialChecked.value;

    /** @type {string} */
    let sizeOptionValueId = '';

    if (handle === 'size') {
      sizeOptionValueId = String(changedActiveTarget.dataset.optionValueId ?? '');
    } else {
      const szInput =
        /** @type {HTMLInputElement | null} */ this.querySelector('input[data-option-name-handle="size"]:checked');

      if (szInput instanceof HTMLInputElement) {
        sizeOptionValueId = String(szInput.dataset.optionValueId ?? '');
      } else {
        const sizeSelect = /** @type {HTMLSelectElement | null} */ (
          this.querySelector('select.variant-option__select[data-option-name-handle="size"]')
        );
        const szOpt =
          (sizeSelect && sizeSelect.selectedOptions[0]) ||
          /** @type {HTMLOptionElement | undefined} */ (sizeSelect?.querySelector?.('option[selected]'));

        sizeOptionValueId = szOpt?.dataset.optionValueId ? String(szOpt.dataset.optionValueId) : '';
      }
    }

    if (!materialValue || !sizeOptionValueId) return;

    /** @type {string} */
    let plateFilterId = '';
    if (handle === 'plate') {
      plateFilterId = String(changedActiveTarget.dataset.optionValueId ?? '');
    } else {
      plateFilterId = this.#getActivePlateOptionValueId() ?? '';
    }

    /** @type {HTMLInputElement[]} */
    let group = /** @type {HTMLInputElement[]} */ (
      Array.from(this.querySelectorAll('input[data-option-name-handle="material"]')).filter((inp) => {
        return (
          inp instanceof HTMLInputElement &&
          inp.value === materialValue &&
          String(inp.dataset.linkedSizeOptionValueId ?? '') === sizeOptionValueId
        );
      })
    );

    if (plateFilterId !== '') {
      group = group.filter(
        (inp) =>
          inp instanceof HTMLInputElement &&
          String(inp.dataset.linkedPlateOptionValueId ?? '') === plateFilterId
      );
    }

    if (!group.length) return;

    const usable = /** @type {HTMLInputElement[]} */ (
      group.filter((inp) => inp instanceof HTMLInputElement && inp.dataset.optionAvailable !== 'false')
    );
    const pick = usable[0] ?? group[0];

    if (pick instanceof HTMLInputElement && pick !== materialChecked) {
      this.updateSelectedOption(pick);
    }
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    if (event.target instanceof HTMLInputElement && event.target.dataset.optionNameHandle === 'material') {
      const linkedPlateOptionValueId = event.target.dataset.linkedPlateOptionValueId;
      if (linkedPlateOptionValueId) {
        const linkedPlateInput = this.querySelector(`[data-option-value-id="${linkedPlateOptionValueId}"]`);
        if (linkedPlateInput instanceof HTMLInputElement) {
          this.updateSelectedOption(linkedPlateInput);
          delete this.dataset.hiddenPlateOptionValueId;
        } else {
          this.dataset.hiddenPlateOptionValueId = linkedPlateOptionValueId;
        }
      } else {
        const firstAvailablePlateInput = Array.from(
          this.querySelectorAll('input[data-option-name-handle="plate"]')
        ).find((input) => input instanceof HTMLInputElement && input.dataset.optionAvailable === 'true');
        if (firstAvailablePlateInput instanceof HTMLInputElement) {
          this.updateSelectedOption(firstAvailablePlateInput);
          delete this.dataset.hiddenPlateOptionValueId;
        } else {
          delete this.dataset.hiddenPlateOptionValueId;
        }
      }

      const linkedSizeOptionValueId = event.target.dataset.linkedSizeOptionValueId;
      if (linkedSizeOptionValueId) {
        const linkedSizeInput = this.querySelector(`[data-option-value-id="${linkedSizeOptionValueId}"]`);
        if (linkedSizeInput instanceof HTMLInputElement) {
          this.updateSelectedOption(linkedSizeInput);
          delete this.dataset.hiddenSizeOptionValueId;
        } else {
          this.dataset.hiddenSizeOptionValueId = linkedSizeOptionValueId;
        }
      } else {
        const firstAvailableSizeInput = Array.from(
          this.querySelectorAll('input[data-option-name-handle="size"]')
        ).find((input) => input instanceof HTMLInputElement && input.dataset.optionAvailable === 'true');
        if (firstAvailableSizeInput instanceof HTMLInputElement) {
          this.updateSelectedOption(firstAvailableSizeInput);
          delete this.dataset.hiddenSizeOptionValueId;
        } else {
          delete this.dataset.hiddenSizeOptionValueId;
        }
      }
    }

    const changedActiveTarget =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption || !(changedActiveTarget instanceof HTMLElement)) return;

    this.#syncExpandedMaterialRadiosForCombinedOptions(changedActiveTarget);

    this.updateSelectedOption(event.target);

    if (changedActiveTarget instanceof HTMLInputElement) {
      const handle = changedActiveTarget.dataset.optionNameHandle;
      if (handle === 'size' || handle === 'plate') {
        this.#syncExpandedMaterialRadiosForCombinedOptions(changedActiveTarget);
      }
    }

    this.dispatchEvent(new VariantSelectedEvent({
      id: selectedOption.dataset.optionValueId ?? '',
    }));

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;
    const isOnFeaturedProductSection = Boolean(this.closest('featured-product-information'));

    const morphElementSelector = loadsNewProduct
      ? 'main'
      : isOnFeaturedProductSection
      ? 'featured-product-information'
      : undefined;

    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), morphElementSelector);

    const url = new URL(window.location.href);

    const variantId = selectedOption.dataset.variantId || null;

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * @typedef {object} FieldsetMeasurements
   * @property {HTMLFieldSetElement} fieldset
   * @property {number | undefined} currentIndex
   * @property {number | undefined} previousIndex
   * @property {number | undefined} currentWidth
   * @property {number | undefined} previousWidth
   */

  /**
   * Gets measurements for a single fieldset (read phase).
   * @param {number} fieldsetIndex
   * @returns {FieldsetMeasurements | null}
   */
  #getFieldsetMeasurements(fieldsetIndex) {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const fieldset = fieldsets[fieldsetIndex];
    const checkedIndices = this.#checkedIndices[fieldsetIndex];
    const radios = this.#radios[fieldsetIndex];

    if (!radios || !checkedIndices || !fieldset) return null;

    const [currentIndex, previousIndex] = checkedIndices;

    return {
      fieldset,
      currentIndex,
      previousIndex,
      currentWidth: currentIndex !== undefined ? radios[currentIndex]?.parentElement?.offsetWidth : undefined,
      previousWidth: previousIndex !== undefined ? radios[previousIndex]?.parentElement?.offsetWidth : undefined,
    };
  }

  /**
   * Applies measurements to a fieldset (write phase).
   * @param {FieldsetMeasurements} measurements
   */
  #applyFieldsetMeasurements({ fieldset, currentWidth, previousWidth, currentIndex, previousIndex }) {
    if (currentWidth) {
      fieldset.style.setProperty('--pill-width-current', `${currentWidth}px`);
    } else if (currentIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-current');
    }

    if (previousWidth) {
      fieldset.style.setProperty('--pill-width-previous', `${previousWidth}px`);
    } else if (previousIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-previous');
    }
  }

  /**
   * Updates the fieldset CSS.
   * @param {number} fieldsetIndex - The fieldset index.
   */
  updateFieldsetCss(fieldsetIndex) {
    if (Number.isNaN(fieldsetIndex)) return;

    const measurements = this.#getFieldsetMeasurements(fieldsetIndex);
    if (measurements) {
      this.#applyFieldsetMeasurements(measurements);
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');

      /** Index into `#radios[fieldset]` (DOM order) — NOT `data-input-index` (sparse for expanded material rows). */
      const domRadioIndex = !Number.isNaN(fieldsetIndex) ? this.#radios[fieldsetIndex]?.indexOf(target) ?? -1 : -1;

      if (!Number.isNaN(fieldsetIndex) && domRadioIndex !== -1) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(domRadioIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // DOM index of the active radio
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
          }

          this.updateFieldsetCss(fieldsetIndex);
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of some specific sections, we need to append section_id=xxxx to the URL
    const SECTION_ID_MAP = {
      'quick-add-component': 'section-rendering-product-card',
      'swatches-variant-picker-component': 'section-rendering-product-card',
      'featured-product-information': this.closest('featured-product-information')?.id,
    };

    const closestSectionId = /** @type {keyof typeof SECTION_ID_MAP} | undefined */ (
      Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId))
    );

    if (closestSectionId) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=${SECTION_ID_MAP[closestSectionId]}&${params.join('&')}`;
    }

    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {string} [morphElementSelector] - The selector of the element to be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, morphElementSelector) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        let newProduct;

        if (morphElementSelector === 'main') {
          this.updateMain(html);
        } else if (morphElementSelector) {
          this.updateElement(html, morphElementSelector);
        } else {
          const { overflowList } = this.refs;
          const wasSwatchesExpanded =
            overflowList instanceof OverflowList && overflowList.getAttribute('disabled') === 'true';

          newProduct = this.updateVariantPicker(html);

          if (wasSwatchesExpanded) {
            const overflowListAfterMorph = overflowList;
            if (overflowListAfterMorph instanceof OverflowList) {
              overflowListAfterMorph.showAll();
            }
          }
        }

        // Dispatch for all paths so product-form-component can reset #variantChangeInProgress
        if (this.selectedOptionId) {
          this.dispatchEvent(
            new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
              html,
              productId: this.dataset.productId ?? '',
              newProduct,
            })
          );
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document | Element} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource, {
      ...MORPH_OPTIONS,
      getNodeKey: (node) => {
        if (!(node instanceof HTMLElement)) return undefined;
        const key = node.dataset.key;
        return key;
      },
    });

    /** Section-render morph replaces fieldset children — rebuild caches or swatch outlines desync from :checked radios. */
    this.#hydrateRadioStateFromDom();

    this.updateVariantPickerCss();

    return newProduct;
  }

  updateVariantPickerCss() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    // Batch all reads first across all fieldsets to avoid layout thrashing
    const measurements = fieldsets.map((_, index) => this.#getFieldsetMeasurements(index)).filter((m) => m !== null);

    // Batch all writes after all reads
    for (const measurement of measurements) {
      this.#applyFieldsetMeasurements(measurement);
    }

    this.#repositionActiveSwatchTooltip();
  }

  /**
   * Re-renders the desired element.
   * @param {Document} newHtml - The new HTML.
   * @param {string} elementSelector - The selector of the element to re-render.
   */
  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);

    if (!element || !newElement) {
      throw new Error(`No new element source found for ${elementSelector}`);
    }

    morph(element, newElement);
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));
    const selectedOptionValueIds = selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });

    const hasPlateInputs = this.querySelector('input[data-option-name-handle="plate"]');
    const hasSizeInputs = this.querySelector('input[data-option-name-handle="size"]');

    if (!hasPlateInputs && this.dataset.hiddenPlateOptionValueId) {
      selectedOptionValueIds.push(this.dataset.hiddenPlateOptionValueId);
    }

    if (!hasSizeInputs && this.dataset.hiddenSizeOptionValueId) {
      selectedOptionValueIds.push(this.dataset.hiddenSizeOptionValueId);
    }

    return selectedOptionValueIds;
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
